import React, { useState, useEffect } from 'react';
import { getCurrentUsername } from '../utils/userManager';
import './OnlineMultiplayer.css';

const OnlineMultiplayer = ({ onAction, webSocketService, currentUsername }) => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [roomCode, setRoomCode] = useState('');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [players, setPlayers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  
  // Use current username from props, fallback to getCurrentUsername if not provided
  const playerName = currentUsername || getCurrentUsername() || 'Player';
  
  // Use the shared WebSocket service from props
  const webSocketServiceRef = React.useRef(webSocketService);

  useEffect(() => {
    // Use the shared WebSocket service
    webSocketServiceRef.current = webSocketService;
    
    if (!webSocketServiceRef.current) {
      setError('WebSocket service not available');
      return;
    }

    // Set up WebSocket event listeners
    webSocketServiceRef.current.on('connected', () => {
      setConnectionStatus('connected');
      setIsConnecting(false);
      setError('');
    });

    webSocketServiceRef.current.on('disconnected', () => {
      setConnectionStatus('disconnected');
      setIsConnecting(false);
      setCurrentRoom(null);
      setPlayers([]);
      setIsHost(false);
    });

    webSocketServiceRef.current.on('roomCreated', (code) => {
      setCurrentRoom(code);
      setIsHost(true);
      setError('');
      setPlayers([{ id: webSocketServiceRef.current.getStatus().playerId, name: playerName, role: 'red' }]);
    });

    webSocketServiceRef.current.on('roomJoined', (code, roomPlayers) => {
      setCurrentRoom(code);
      setPlayers(roomPlayers || []);
      setIsHost(false);
      setError('');
    });

    webSocketServiceRef.current.on('playerJoined', (player) => {
      setPlayers(prev => {
        const newPlayers = [...prev, player];
        console.log('Player joined, total players:', newPlayers.length);
        return newPlayers;
      });
    });

    webSocketServiceRef.current.on('gameStarted', (gameState) => {
      console.log('Game started event in OnlineMultiplayer:', gameState);
      setGameStarting(false); // Reset the starting state
      // The Connect4Game component will handle the actual game transition
      // We just need to clean up the multiplayer UI state
    });

    webSocketServiceRef.current.on('error', (err) => {
      setError(typeof err === 'string' ? err : err.message || 'Connection error occurred');
      setIsConnecting(false);
    });

    // Check if already connected, if not auto-connect
    if (!webSocketServiceRef.current.getStatus().isConnected) {
      setIsConnecting(true);
      setConnectionStatus('connecting');
      webSocketServiceRef.current.connect();
    } else {
      setConnectionStatus('connected');
    }

    // No cleanup needed since we're using a shared service
    return () => {
      // Don't disconnect the shared service
    };
  }, [onAction, playerName, webSocketService]);

  const handleConnect = () => {
    if (webSocketServiceRef.current) {
      setIsConnecting(true);
      setConnectionStatus('connecting');
      webSocketServiceRef.current.connect();
    }
  };

  const handleCreateRoom = () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (webSocketServiceRef.current && connectionStatus === 'connected') {
      webSocketServiceRef.current.createRoom(playerName.trim());
    }
  };

  const handleJoinRoom = () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!roomCode.trim()) {
      setError('Please enter room code');
      return;
    }
    
    if (webSocketServiceRef.current && connectionStatus === 'connected') {
      webSocketServiceRef.current.joinRoom(roomCode.trim().toUpperCase(), playerName.trim());
    }
  };

  const handleStartGame = () => {
    if (gameStarting) {
      console.log('Game is already starting, ignoring duplicate request');
      return;
    }
    
    if (!webSocketServiceRef.current || !isHost || players.length < 2) {
      console.log('Cannot start game:', { 
        hasService: !!webSocketServiceRef.current, 
        isHost, 
        playerCount: players.length 
      });
      return;
    }
    
    console.log('Starting game manually...');
    setGameStarting(true);
    
    // Use a timeout to prevent multiple rapid calls
    setTimeout(() => {
      const success = webSocketServiceRef.current.startGame();
      if (success) {
        console.log('Game start request sent successfully');
      } else {
        console.log('Failed to send game start request');
        setGameStarting(false);
      }
    }, 100);
  };

  const handleLeaveRoom = () => {
    if (webSocketServiceRef.current && currentRoom) {
      webSocketServiceRef.current.leaveRoom();
      setCurrentRoom(null);
      setPlayers([]);
      setIsHost(false);
    }
  };

  const copyRoomCode = async () => {
    if (currentRoom) {
      try {
        await navigator.clipboard.writeText(currentRoom);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      } catch (err) {
        console.error('Failed to copy room code:', err);
      }
    }
  };

  return (
    <div className="online-multiplayer">
      <div className="online-background">
        <div className="online-content">
          <div className="online-header">
            <h2>Online Multiplayer</h2>
          </div>
          
          {/* Connection Status */}
          <div className={`connection-status ${connectionStatus}`}>
            <div className="status-indicator">
              {connectionStatus === 'connected' && <span className="status-icon">✓</span>}
              {connectionStatus === 'connecting' && <div className="loading-spinner"></div>}
              {connectionStatus === 'disconnected' && <span className="status-icon">✗</span>}
            </div>
            <span className="status-text">
              {connectionStatus === 'connected' && '🟢 Connected to server'}
              {connectionStatus === 'connecting' && '🟡 Connecting to server...'}
              {connectionStatus === 'disconnected' && '🔴 Disconnected from server'}
            </span>
          </div>

          {error && (
            <div className="error-message animate-shake">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Not Connected State */}
          {connectionStatus === 'disconnected' && (
            <div className="connection-section animate-fade-in">
              <div className="connection-card">
                <div className="card-icon">🔗</div>
                <h3>Connect to Game Server</h3>
                <button 
                  className={`connect-btn ${isConnecting ? 'loading' : ''}`}
                  onClick={handleConnect}
                  disabled={isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <div className="btn-spinner"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">🔗</span>
                      Connect to Server
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Connected but no room */}
          {connectionStatus === 'connected' && !currentRoom && (
            <div className="room-section animate-slide-up">
              <div className="room-options">
                <div className="create-room-card">
                  <div className="card-header">
                    <div className="card-icon">🏠</div>
                    <h3>Create New Room</h3>
                  </div>
                  <p>Host a new game and invite a friend to join</p>
                  <div className="card-features">
                    <span className="feature">🎯 Instant room creation</span>
                    <span className="feature">🔗 Shareable room code</span>
                    <span className="feature">👑 You control the game</span>
                  </div>
                  <button 
                    className="room-btn create"
                    onClick={handleCreateRoom}
                  >
                    <span className="btn-icon">🏠</span>
                    Create Room
                  </button>
                </div>

                <div className="join-room-card">
                  <div className="card-header">
                    <div className="card-icon">🚪</div>
                    <h3>Join Existing Room</h3>
                  </div>
                  <p>Enter a friend's room code to join their game</p>
                  <div className="join-input-group">
                    <input
                      type="text"
                      value={roomCode}
                      onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                      placeholder="ROOM CODE"
                      maxLength={6}
                      className="room-code-input"
                    />
                  </div>
                  <button 
                    className="room-btn join"
                    onClick={handleJoinRoom}
                    disabled={!roomCode.trim()}
                  >
                    <span className="btn-icon">🚪</span>
                    Join Room
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* In Room State */}
          {currentRoom && (
            <div className="in-room-section animate-slide-up">
              <div className="room-header">
                <div className="room-code-display">
                  <h3>🏠 Room Code</h3>
                  <div className="room-code-container">
                    <span className="room-code">{currentRoom}</span>
                    <button 
                      className={`copy-btn ${showCopiedMessage ? 'copied' : ''}`}
                      onClick={copyRoomCode}
                      title="Copy room code"
                    >
                      {showCopiedMessage ? '✓' : '📋'}
                    </button>
                  </div>
                  {showCopiedMessage && (
                    <span className="copied-message animate-fade-in">Copied to clipboard!</span>
                  )}
                </div>
                
                {isHost && (
                  <div className="host-badge animate-pulse">
                    <span className="crown">👑</span>
                    <span>You are the host</span>
                  </div>
                )}
              </div>

              <div className="players-section">
                <h4>Players in Room ({players.length}/2)</h4>
                <div className="players-grid">
                  {players.map((player, index) => (
                    <div key={player.id} className={`player-card ${index === 0 ? 'red' : 'yellow'} animate-bounce-in`}>
                      <div className="player-avatar">
                        <div className="player-piece"></div>
                        <span className="player-number">{index + 1}</span>
                      </div>
                      <div className="player-info">
                        <span className="player-name">{player.name}</span>
                        <span className="player-role">{index === 0 ? 'Red Player' : 'Yellow Player'}</span>
                        {player.id === webSocketServiceRef.current?.getStatus().playerId && (
                          <span className="you-badge">You</span>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {players.length < 2 && (
                    <div className="waiting-slot animate-pulse">
                      <div className="waiting-avatar">
                        <div className="loading-dots"></div>
                      </div>
                      <div className="waiting-info">
                        <span className="waiting-text">Waiting for player...</span>
                        <span className="waiting-hint">Share the room code with a friend!</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="game-controls">
                {isHost && players.length >= 2 && (
                  <button 
                    className="start-game-btn animate-glow"
                    onClick={handleStartGame}
                    disabled={gameStarting}
                  >
                    <span className="btn-icon">🎮</span>
                    {gameStarting ? 'Starting Game...' : 'Start Game'}
                  </button>
                )}
                
                {!isHost && players.length < 2 && (
                  <div className="waiting-message">
                    <div className="waiting-icon">⏳</div>
                    <p>Waiting for the host to start the game...</p>
                  </div>
                )}

                <button 
                  className="leave-room-btn"
                  onClick={handleLeaveRoom}
                >
                  <span className="btn-icon">🚪</span>
                  Leave Room
                </button>
              </div>
            </div>
          )}

          {/* Back to Menu */}
          <div className="online-footer">
            <button 
              className="back-button"
              onClick={() => onAction('menu')}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineMultiplayer;
